// HomeSearchHistory.jsx
import React from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CloseButton,
  HStack,
  Icon,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FaChartLine, FaClipboardList } from 'react-icons/fa';

const HomeSearchHistory = ({
  currentUser,
  isLoadingHistory,
  searchLoading,
  currentPage,
  itemsPerPage,
  totalPages,
  indexOfFirstItem,
  indexOfLastItem,
  filteredSearches,
  currentItems,
  isPremiumUser,
  handlePageChange,
  onDeleteSearch,
  onClickSearch,
}) => {
  if (isLoadingHistory) {
    return (
      <Box mb={8}>
        <Card variant='outline' bg='white' p={6}>
          <VStack spacing={4} align='center'>
            <Text fontSize='lg' fontWeight='medium' color='gray.700'>
              Loading Search History...
            </Text>
            <Spinner size='md' color='blue.500' />
          </VStack>
        </Card>
      </Box>
    );
  }

  return filteredSearches.length > 0 ? (
    <Box mb={8}>
      <Card variant='outline' bg='white' p={6}>
        <VStack spacing={4} align='stretch'>
          <HStack justifyContent='space-between'>
            <HStack>
              <Text fontSize='lg' fontWeight='medium' color='gray.700'>
                Previous Searches
              </Text>
              {searchLoading && <Spinner size='sm' color='blue.500' ml={2} />}
            </HStack>
            {currentUser && (
              <Badge colorScheme='green' variant='subtle' px={2} py={1} borderRadius='md'>
                {currentUser.subscriptionType === 'premium' ? 'Premium' : 'Free'}
              </Badge>
            )}
          </HStack>

          {currentUser && currentUser.subscriptionType !== 'premium' && (
            <Alert status='info' size='sm' mb={3} borderRadius='md'>
              <AlertIcon />
              <Text fontSize='sm'>
                {/*Free accounts are limited to 6 saved searches. Upgrade to Premium for unlimited
                searches.*/}
                Free accounts are limited to 6 saved searches while we are in test mode.
              </Text>
            </Alert>
          )}

          {isPremiumUser && filteredSearches.length > itemsPerPage && (
            <HStack justifyContent='flex-end' mb={2}>
              <Text fontSize='sm' color='gray.600'>
                Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredSearches.length)}{' '}
                of {filteredSearches.length}
              </Text>
            </HStack>
          )}

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {currentItems.map((search, index) => (
              <Card
                key={search.id || index}
                variant='outline'
                bg='gray.50'
                _hover={searchLoading ? {} : { bg: 'gray.100' }}
                position='relative'
                opacity={searchLoading ? 0.7 : 1}
                cursor={searchLoading ? 'not-allowed' : 'pointer'}
                onClick={() => onClickSearch(search)}
                p={4}
              >
                <CloseButton
                  position='absolute'
                  right='2'
                  top='2'
                  size='sm'
                  isDisabled={searchLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (searchLoading) return;
                    onDeleteSearch(search.id || index);
                  }}
                />
                <Box>
                  <VStack spacing={2} align='start' w='100%'>
                    <HStack spacing={2} align='center' w='100%'>
                      <Text fontWeight='medium' color='gray.700'>
                        {search.address}
                      </Text>
                      <Badge colorScheme='blue' fontSize='xs'>
                        {search.postcode}
                      </Badge>
                    </HStack>
                    {search.flat_number && (
                      <Text fontSize='sm' color='gray.500'>
                        Flat: {search.flat_number}
                      </Text>
                    )}
                    <Text
                      position='absolute'
                      bottom='2'
                      left='4'
                      fontSize='xs'
                      color='gray.400'
                      zIndex='1'
                    >
                      ID: {search.id || 'None'}
                    </Text>
                    <HStack spacing={2} mt={1} w='100%' justify='flex-start'>
                      <Box bg='green.50' p={1} borderRadius='md' boxShadow='xs'>
                        <HStack spacing={1}>
                          <Icon as={FaChartLine} color='green.500' boxSize={4} />
                          {search.annual_growth_rate != null ? (
                            <Text
                              fontSize='xs'
                              fontWeight='bold'
                              color={
                                Number(search.annual_growth_rate) >= 0 ? 'green.600' : 'red.600'
                              }
                            >
                              {Number(search.annual_growth_rate) >= 0 ? '+' : ''}
                              {typeof search.annual_growth_rate === 'number'
                                ? search.annual_growth_rate.toFixed(1)
                                : Number(search.annual_growth_rate).toFixed(1)}
                              %
                            </Text>
                          ) : (
                            <Text fontSize='xs' fontWeight='bold' color='gray.600'>
                              --
                            </Text>
                          )}
                        </HStack>
                      </Box>
                      <Box bg='purple.50' p={1} borderRadius='md' boxShadow='xs'>
                        <HStack spacing={1}>
                          <Icon as={FaClipboardList} color='purple.500' boxSize={4} />
                          {search.planning_success_rate != null ? (
                            <Text
                              fontSize='xs'
                              fontWeight='bold'
                              color={
                                Number(search.planning_success_rate) >= 80
                                  ? 'green.600'
                                  : Number(search.planning_success_rate) >= 70
                                  ? 'yellow.600'
                                  : 'red.600'
                              }
                            >
                              {Math.round(Number(search.planning_success_rate))}%
                            </Text>
                          ) : (
                            <Text fontSize='xs' fontWeight='bold' color='gray.600'>
                              --
                            </Text>
                          )}
                        </HStack>
                      </Box>
                    </HStack>
                  </VStack>
                </Box>
              </Card>
            ))}
          </SimpleGrid>
          {filteredSearches.length > itemsPerPage && (
            <HStack spacing={2} justifyContent='center' mt={4}>
              <Button
                size='sm'
                onClick={() => handlePageChange(1)}
                isDisabled={currentPage === 1}
                colorScheme='blue'
                variant='outline'
              >
                First
              </Button>
              <Button
                size='sm'
                onClick={() => handlePageChange(currentPage - 1)}
                isDisabled={currentPage === 1}
                colorScheme='blue'
                variant='outline'
              >
                Previous
              </Button>
              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <Button
                      key={pageNum}
                      size='sm'
                      onClick={() => handlePageChange(pageNum)}
                      colorScheme='blue'
                      variant={currentPage === pageNum ? 'solid' : 'outline'}
                    >
                      {pageNum}
                    </Button>
                  );
                } else if (
                  (pageNum === currentPage - 2 && currentPage > 3) ||
                  (pageNum === currentPage + 2 && currentPage < totalPages - 2)
                ) {
                  return <Text key={pageNum}>...</Text>;
                }
                return null;
              })}
              <Button
                size='sm'
                onClick={() => handlePageChange(currentPage + 1)}
                isDisabled={currentPage === totalPages}
                colorScheme='blue'
                variant='outline'
              >
                Next
              </Button>
              <Button
                size='sm'
                onClick={() => handlePageChange(totalPages)}
                isDisabled={currentPage === totalPages}
                colorScheme='blue'
                variant='outline'
              >
                Last
              </Button>
            </HStack>
          )}
        </VStack>
      </Card>
    </Box>
  ) : currentUser ? (
    <Box mb={8}>
      <Card variant='outline' bg='white' p={6}>
        <VStack spacing={4} align='center'>
          <Text fontSize='lg' fontWeight='medium' color='gray.700'>
            No Search History
          </Text>
          <Text color='gray.500'>Your search history will appear here</Text>
        </VStack>
      </Card>
    </Box>
  ) : null;
};

export default HomeSearchHistory;
